/* Physical projection for By Layla Saleh. GLB coordinates remain in metres.
 * The camera, not the glasses, is calibrated. Iris-based calibration is an
 * estimate; an entered distance PD provides the shopper's measured reference.
 * Iris prior: research.google/blog/mediapipe-iris-real-time-iris-tracking-depth-estimation/
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.VTONScale = factory();
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';
  function finite(n) { return typeof n === 'number' && isFinite(n); }
  function point(p) { return p && finite(p.x) && finite(p.y) && finite(p.z); }
  function median(a) {
    var s = a.slice().sort(function (x, y) { return x - y; });
    return s.length ? (s[Math.floor((s.length - 1) / 2)] + s[Math.floor(s.length / 2)]) / 2 : NaN;
  }
  function distance(a, b, w, h) { return Math.hypot((a.x - b.x) * w, (a.y - b.y) * h); }
  function midpoint(a, b) { return { x:(a.x+b.x)/2, y:(a.y+b.y)/2, z:(a.z+b.z)/2 }; }
  function cover(w, h, vw, vh) {
    var s = Math.max(w / vw, h / vh);
    return { scale:s, width:vw*s, height:vh*s, x:(w-vw*s)/2, y:(h-vh*s)/2 };
  }
  // Recover depth per unit eye baseline from two camera rays and the head's
  // horizontal axis. Dividing screen width by cos(yaw) only works at the
  // optical centre: away from it, a head turn otherwise becomes a size change.
  function baselineDepth(a, b, axis, w, h, focal) {
    var ax=(a.x*w-w/2)/focal, ay=-(a.y*h-h/2)/focal;
    var bx=(b.x*w-w/2)/focal, by=-(b.y*h-h/2)/focal;
    var dx=bx-ax, dy=by-ay, cx=(ax+bx)/2, cy=(ay+by)/2;
    var vv=dx*dx+dy*dy, cc=cx*cx+cy*cy+1, vc=dx*cx+dy*cy;
    var av=axis[0]*dx+axis[1]*dy, ac=axis[0]*cx+axis[1]*cy-axis[2];
    var determinant=vv*cc-vc*vc;
    if (!(determinant>1e-8)) return null;
    var depth=(av*cc-ac*vc)/determinant;
    var difference=(ac*vv-av*vc)/determinant;
    var residual=Math.hypot(depth*dx+difference*cx-axis[0],
      depth*dy+difference*cy-axis[1],-difference-axis[2]);
    return depth>0 && Math.abs(difference)<depth*1.5 && residual<0.15 ? depth : null;
  }
  function measure(lm, fm, w, h) {
    if (!lm || !fm || fm.length !== 16 || !(w > 0 && h > 0)) return null;
    var needed = [33,133,263,362,168,159,145,386,374,468,469,471,473,474,476];
    if (needed.some(function(i) { return !point(lm[i]); }) || !Array.from(fm).every(finite)) return null;
    var xNorm = Math.hypot(fm[0],fm[1],fm[2]), yNorm = Math.hypot(fm[4],fm[5],fm[6]);
    if (!(xNorm>0 && yNorm>0)) return null;
    var left = midpoint(lm[33],lm[133]), right = midpoint(lm[263],lm[362]);
    var focal=w/(2*Math.tan(Math.PI/6)), axis=[fm[0]/xNorm,fm[1]/xNorm,fm[2]/xNorm];
    var cornerDepth=baselineDepth(left,right,axis,w,h,focal);
    var irisDepth=baselineDepth(lm[468],lm[473],axis,w,h,focal);
    if (!cornerDepth || !irisDepth) return null;
    var cornerPx=focal/cornerDepth, eyePx=focal/irisDepth;
    var horizontal=distance(lm[468],lm[473],w,h)/eyePx;
    var vertical=Math.hypot(fm[4],fm[5])/yNorm;
    var cx=((left.x+right.x)*w/2-w/2)/focal;
    var cy=-((left.y+right.y)*h/2-h/2)/focal;
    var zNorm=Math.hypot(fm[8],fm[9],fm[10]);
    var facing=(-cx*fm[8]-cy*fm[9]+fm[10])/(Math.hypot(cx,cy,1)*zNorm);
    if (!(horizontal>0.35 && facing>0.35 && vertical>0.6)) return null;
    var irisL = distance(lm[469],lm[471],w,h), irisR = distance(lm[474],lm[476],w,h);
    var irisPx = (irisL + irisR) / 2;
    if (!(cornerPx > 20 && cornerPx < w * 0.85)) return null;
    var open = distance(lm[159],lm[145],w,h) / distance(lm[33],lm[133],w,h) > 0.12 &&
               distance(lm[386],lm[374],w,h) / distance(lm[263],lm[362],w,h) > 0.12;
    return { cornerPx:cornerPx, eyePx:eyePx, eyeZ:(left.z+right.z)/2,
      irisPx:irisPx, horizontal:horizontal, vertical:vertical,
      calibratable:facing > 0.96 && open && irisPx >= 4 &&
        Math.max(irisL,irisR)/Math.min(irisL,irisR) < 1.2 };
  }
  function Calibration(pd) { this.reset(pd); }
  Calibration.prototype.reset = function (pd) {
    // Do not silently clamp a mistyped measurement into a plausible one.
    this.pd = finite(pd) && pd >= 45 && pd <= 80 ? pd : null;
    this.samples = []; this.cornerMm = null; this.started = null;
  };
  Calibration.prototype.update = function (m, time) {
    if (!m || !finite(time)) return null;
    if (!this.cornerMm) {
      if (!m.calibratable) { this.samples=[]; this.started=null; return null; }
      var pd = this.pd || m.eyePx / (m.irisPx / m.horizontal) * 11.7;
      var cornerMm = m.cornerPx / m.eyePx * pd;
      if (!(pd >= 45 && pd <= 80 && cornerMm >= 40 && cornerMm <= 85)) return null;
      if (this.started === null) this.started=time;
      this.samples.push(cornerMm);
      if (this.samples.length > 24) this.samples.shift();
      if (this.samples.length < 16 || time-this.started < 0.55) return null;
      var middle=median(this.samples);
      if (median(this.samples.map(function(v){return Math.abs(v-middle);})) / middle > 0.025) return null;
      this.cornerMm=middle;
    }
    // The person's reference is locked for the session. Head turns never
    // change this reference, and product dimensions never participate here.
    var scale=m.cornerPx/this.cornerMm;
    return finite(scale) && scale > 0 ? scale : null;
  };
  function OneEuro(cutoff,beta) { this.cutoff=cutoff; this.beta=beta; this.reset(); }
  OneEuro.prototype.reset=function(){this.value=null;this.raw=null;this.time=null;this.derivative=0;};
  OneEuro.prototype.filter=function(value,time){
    if (!finite(value) || !finite(time)) return this.value;
    if (this.time===null || time-this.time > 0.25 || time <= this.time) {
      this.value=value;this.raw=value;this.time=time;this.derivative=0;return value;
    }
    var dt=time-this.time, da=1/(1+1/(2*Math.PI*dt));
    this.derivative=da*(value-this.raw)/dt+(1-da)*this.derivative;
    var a=1/(1+1/(2*Math.PI*(this.cutoff+this.beta*Math.abs(this.derivative))*dt));
    this.value+=a*(value-this.value);this.raw=value;this.time=time;
    return this.value;
  };
  return { version:6, cover:cover, measure:measure, Calibration:Calibration, OneEuro:OneEuro, median:median };
}));
